import FetchBase from '../Api/FetchBase'
import { observable, action } from 'mobx'
import {
  API_ENDPOINTS,
  DEFAULT_PAGE_START,
  ERROR_CODE,
} from '../Utils/constants'
import { generateFormData } from '../Utils/helpers'

export default class SpacesStore extends FetchBase {
  @observable whichProject = {}
  @observable spaces = []
  @observable isCheckAll = false
  @observable spaceDownloadFileList = []
  @observable newSpaceList = []
  @observable spacesTotal = 0
  @observable spacesCurrentPage = DEFAULT_PAGE_START
  @observable tags = []
  @observable spaceTemplates = []
  @observable spaceLoader = false
  @observable isUserAssignedSpaceCard = false

  @observable spaceTagLoader = false
  @observable spacesSearchText = undefined

  @observable ordering = undefined
  @observable orderingBy = undefined
  @observable spacesFilters = {}

  @observable spacesTabListSearchText = undefined
  @observable spacesTabListTotal = 0
  @observable spacesTabList = []
  @observable isUserAssignedSpaceList = false
  @observable spaceListIsMultiSelect = false
  @observable spaceListDownloadFileList = []
  @observable spaceListDownloadCategory = []
  @observable spacesFullListLoader = false

  @observable spacesTabListFilters = {}
  @observable addSpaceTemplateLoader = false

  @observable importSpaces = []
  @observable importSpacesFailures = []
  @observable importSpacesWithEquipmentFailures = []

  @observable importSpaceWithTaskFileLoader = false
  @observable bulkSpaceLoader = false

  @action.bound
  setOrdering = (value = undefined) => {
    this.ordering = value
  }

  @action.bound
  setOrderingBy = (value = undefined) => {
    this.orderingBy = value
  }

  @action.bound
  setSpacesFilters = (values = {}) => {
    this.spacesFilters = values
  }

  @action.bound
  setSpacesCurrentPage = (page = DEFAULT_PAGE_START) => {
    this.spacesCurrentPage = page
  }

  @action.bound
  setSpacesSearchText = (value = undefined) => {
    this.spacesSearchText = value
  }

  setSpaceLoader = (value = false) => {
    this.spaceLoader = value
  }

  setSpaceTagLoader = (value = false) => {
    this.spaceTagLoader = value
  }

  addSpace = async (values) => {
    try {
      let response = await this.post(API_ENDPOINTS.ADD_BULK_SPACE, values)
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  addSpaceViaTemplates = async (values) => {
    try {
      let response = await this.post(
        API_ENDPOINTS.CREATE_SPACE_WITH_TEMPLATES,
        values
      )
      if (response.status) {
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      return [false, undefined]
    }
  }

  deleteSpace = async (id) => {
    try {
      let response = await this.post(API_ENDPOINTS.DELETE_SPACE, {
        id,
      })
      if (response.status) {
        return [true, undefined]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  locallyDeleteSpace = async (id) => {
    this.spacesTotal--
    this.spacesTabListTotal--
    this.spaces = (this.spaces || []).filter((space) => space?.id !== id)
    this.spacesTabList = (this.spacesTabList || []).filter(
      (space) => space?.id !== id
    )
  }

  updateSpace = async (values) => {
    try {
      let response = await this.post(API_ENDPOINTS.UPDATE_SPACE, values || {})
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  locallyUpdateSpace = async (newSpace = {}) => {
    const newSpaceId = newSpace?.id

    this.spaces = (this.spaces || []).map((space) => {
      if (space.id === newSpaceId) {
        return { ...space, ...newSpace }
      }
      return space
    })

    this.spacesTabList = (this.spacesTabList || []).map((space) => {
      if (space.id === newSpaceId) {
        return { ...space, ...newSpace }
      }
      return space
    })
  }

  getSpaceFilters = () => {
    let temp = {}
    let keys = Object.keys(this.spacesFilters || {})
    for (let filter of keys) {
      if (this.spacesFilters[filter] !== undefined) {
        temp[filter] = this.spacesFilters[filter]
      }
    }
    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }

    return temp
  }

  // Drag
  @action.bound
  reorderDataSourceUpdate = async (newOrder = []) => {
    if (newOrder.length) this.spaces = newOrder
  }

  getSpaces = async (projectId, signal) => {
    try {
      this.spaceLoader = true
      let options = this.getSpaceFilters() || {}
      this.spacesSearchText && (options['search'] = this.spacesSearchText)

      let response = await this.post(
        API_ENDPOINTS.GET_SPACE,
        {
          projectId,
          pageNo: Math.max(0, this.spacesCurrentPage - 1),
          pageLimit: 0,
          ...options,
        },
        signal
      )
      if (response.status) {
        this.spaces = response?.data?.spaces || []
        this.spacesTotal = response?.data?.total || 0
        this.whichProject = response?.data?.project || {}
        this.isUserAssignedSpaceCard = response?.isUserAssigned || false
        return [true, response?.data || []]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      return [false, undefined]
    } finally {
      this.spaceLoader = false
    }
  }

  @action.bound
  setSpaceDownloadFile = async (list = [], totalFiles) => {
    this.spaceDownloadFileList = list
    this.isCheckAll = list?.length === totalFiles
  }

  @action.bound
  setSpaceCategory = async (list = []) => {
    this.spaceDownloadCategory = list
  }

  getSpaceExportFilters = () => {
    let temp = {}
    let keys = Object.keys(this.spaceDownloadCategory || {})
    for (let filter of keys) {
      if (this.spaceDownloadCategory[filter] !== undefined) {
        temp[this.spaceDownloadCategory[filter]] = true
      } else {
        temp[this.spaceDownloadCategory[filter]] = false
      }
    }
    temp['spaceId'] = this.spaceDownloadFileList
    return temp
  }

  @action.bound
  getAllSpacesExportData = async (
    { exportFileType = 'csv' } = {},
    projectId = undefined
  ) => {
    try {
      let filters = this.getSpaceExportFilters() || {}

      let options = { ...filters }
      options['projectId'] = projectId
      options['exportFileType'] = exportFileType
      let response = await this.post(API_ENDPOINTS.EXPORT_SPACES, options)
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  reorderSpacesLocally = (newSpaces) => {
    this.spaces = [...newSpaces]
  }

  sortingSpacesLocally = (sortType) => {
    let sortList = this.spaces
    if (sortType === 'ASC') {
      this.spaces = sortList.reverse()
    } else if (sortType === 'DESC') {
      this.spaces = sortList.reverse()
    }
  }

  getSpaceTemplates = async (fId, netowkOnly = true) => {
    if (!netowkOnly) {
      if (this.spaceTemplates?.length) {
        return [true, this.spaceTemplates]
      }
    }
    try {
      let response = await this.get(API_ENDPOINTS.IMPORT_SPACE_TEMPLATES, {
        spaceTemplateFolderId: fId,
        pageLimit: 0,
      })
      if (response.status) {
        this.spaceTemplates = response.data.spaceTemplates
        return [true, response.data.spaceTemplates]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      this.spaceTemplates = []
      return [false, undefined]
    }
  }

  addSpaceTemplatesOnEdit = async (spaceId, templateIds) => {
    try {
      let response = await this.post(
        API_ENDPOINTS.ADD_SPACE_TEMPLATE_EXISTING_SPACE,
        {
          spaceId,
          templateIdsArr: templateIds,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  getTags = async (projectId, signal) => {
    try {
      let response = await this.get(API_ENDPOINTS.GET_SPACE_TAGS, {}, signal)
      if (response.status) {
        this.tags = response.data
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {}
  }

  addTag = async (values) => {
    try {
      this.setSpaceTagLoader(true)
      let response = await this.post(API_ENDPOINTS.ADD_TAG, values || {})
      if (response.status) {
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      return [false, undefined]
    } finally {
      this.setSpaceTagLoader(false)
    }
  }

  addTagLocally = async (spaceId, tag) => {
    this.spaces = (this.spaces || []).map((space) => {
      if (space.id === spaceId) {
        space.tagId = tag?.id
        space.spaceTag = tag
        return space
      } else {
        return space
      }
    })

    this.spacesTabList = (this.spacesTabList || []).map((space) => {
      if (space.id === spaceId) {
        space.tagId = tag?.id
        space.spaceTag = tag
        return space
      } else {
        return space
      }
    })
  }

  updateTag = async (values) => {
    try {
      this.setSpaceTagLoader(true)
      let response = await this.post(API_ENDPOINTS.UPDATE_SPACE, values || {})
      if (response.status) {
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      return [false, undefined]
    } finally {
      this.setSpaceTagLoader(false)
    }
  }

  getAllDropdownSpacesList = async (projectId, signal) => {
    try {
      this.spacesFullListLoader = true
      let response = await this.post(
        API_ENDPOINTS.GET_SPACE_LIST_DROPDOWN,
        {
          projectId,
          pageLimit: 0,
        },
        signal
      )
      if (response.status) {
        this.spacesFullList = response.data.spaces
        return [true, response.data.spaces]
      }
      this.spacesFullList = []
      return [false, undefined]
    } catch (err) {
      this.spacesFullList = []
      return [false, undefined]
    } finally {
      this.spacesFullListLoader = false
    }
  }

  @action.bound
  copySpaceTemplateViaSpace = async (values) => {
    try {
      this.addSpaceTemplateLoader = true
      let response = await this.post(API_ENDPOINTS.COPY_SPACE_TEMPLATE, values)
      if (response.status) {
        this.addSpaceTemplateLoader = false
        return [true, response.data]
      } else {
        this.addSpaceTemplateLoader = false
        return [false, undefined]
      }
    } catch (err) {
      this.addSpaceTemplateLoader = false
      return [false, undefined]
    }
  }

  @action.bound
  createDuplicateSpace = async (spaceIds) => {
    try {
      let response = await this.post(API_ENDPOINTS?.DUPLICTE_SPACE, {
        id: spaceIds,
      })
      if (response?.status) {
        return [true, response?.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  deleteBulkSpace = async (spaceIds) => {
    try {
      let response = await this.post(API_ENDPOINTS?.DELETE_BULK_SPACE, {
        id: spaceIds,
      })
      if (response.status) {
        this.spaceDownloadFileList = 0
        this.spaceListDownloadFileList = 0
        return [true, response?.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  locallyDeleteBulkSpace = async (spaceID) => {
    this.spacesTotal = this.spacesTotal - spaceID?.length
    this.spacesTabListTotal = this.spacesTabListTotal - spaceID?.length
    spaceID.forEach((id) => {
      this.spaces = this.spaces.filter((space) => space?.id !== id)
      this.spacesTabList = this.spacesTabList.filter(
        (space) => space?.id !== id
      )
    })
  }

  @action.bound
  bulkSpaceEdit = async (values = {}) => {
    try {
      this.bulkSpaceLoader = true
      let response = await this.post(API_ENDPOINTS?.UPDATE_BULK_SPACE, {
        ...values,
      })
      if (response?.status) {
        return [true, response]
      }
      return [false, undefined]
    } catch (err) {
      this.bulkSpaceLoader = false
      return [false, undefined]
    } finally {
      this.bulkSpaceLoader = false
    }
  }

  @action.bound
  bulkSpacePopUpEdit = async (values = {}) => {
    try {
      this.spaceLoader = true
      let response = await this.post(API_ENDPOINTS?.UPDATE_BULK_POP_UP_SPACE, {
        ...values,
      })
      if (response?.status) {
        return [true, response?.data]
      }
      return [false, undefined]
    } catch (err) {
      this.spaceLoader = false
      return [false, undefined]
    } finally {
      this.spaceLoader = false
    }
  }

  @action.bound
  reorderSpaces = async ({ projectId, newSpaces = this.spaces } = {}) => {
    if (newSpaces?.length < 2) {
      return [true, undefined]
    }
    try {
      let response = await this.post(API_ENDPOINTS.REORDER_SPACE, {
        projectId,
        newSpaceIdsInOrder: newSpaces.map((space) => space?.id).filter(Boolean),
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  // Space list-----------------------------------------------------------

  @action.bound
  setSpacesTabFilters = (values = {}) => {
    this.spacesTabListFilters = values
  }

  @action.bound
  setSpacesTabSearchText = (value = undefined) => {
    this.spacesTabListSearchText = value
  }

  getSpaceTabFilters = () => {
    let temp = {}
    let keys = Object.keys(this.spacesTabListFilters || {})
    for (let filter of keys) {
      if (this.spacesTabListFilters[filter] !== undefined) {
        temp[filter] = this.spacesTabListFilters[filter]
      }
    }
    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }

    return temp
  }

  // Drag
  @action.bound
  reorderDataSourceTabUpdate = async (newOrder = []) => {
    if (newOrder.length) this.spacesTabList = newOrder
  }

  getSpacesTab = async (projectId, signal) => {
    try {
      this.spaceLoader = true
      let options = this.getSpaceTabFilters() || {}
      this.spacesTabListSearchText &&
        (options['search'] = this.spacesTabListSearchText)

      let response = await this.post(
        API_ENDPOINTS.GET_SPACE,
        {
          projectId,
          pageNo: Math.max(0, this.spacesCurrentPage - 1),
          pageLimit: 0,
          ...options,
        },
        signal
      )
      if (response.status) {
        this.spacesTabList = response?.data?.spaces || []
        this.spacesTabListTotal = response?.data?.total || 0
        this.whichProject = response?.data?.project || {}
        this.isUserAssignedSpaceList = response?.isUserAssigned || false
        return [true, response?.data || []]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      return [false, undefined]
    } finally {
      this.spaceLoader = false
    }
  }
  @action.bound
  setSpaceIsMultiSelect = async (visible = false) => {
    this.spaceListIsMultiSelect = visible
  }
  @action.bound
  setSpaceTabDownloadFileList = async (list = []) => {
    this.spaceListDownloadFileList = list
  }

  @action.bound
  setSpaceTabCategory = async (list = []) => {
    this.spaceListDownloadCategory = list
  }

  getSpaceTabExportFilters = () => {
    let temp = {}

    let keys = Object.keys(this.spaceListDownloadCategory || {})
    for (let filter of keys) {
      if (this.spaceListDownloadCategory[filter] !== undefined) {
        temp[this.spaceListDownloadCategory[filter]] = true
      }
    }
    temp['spaceId'] = this.spaceListDownloadFileList
    return temp
  }

  @action.bound
  getAllSpacesListExportData = async (
    { exportFileType = 'csv' } = {},
    projectId = undefined
  ) => {
    try {
      let filters = this.getSpaceTabExportFilters() || {}
      let options = { ...filters }
      options['projectId'] = projectId
      options['exportFileType'] = exportFileType
      let response = await this.post(API_ENDPOINTS.EXPORT_SPACES, options)
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  reorderSpacesTabLocally = (newSpaces) => {
    this.spacesTabList = [...newSpaces]
  }

  sortingSpacesTabLocally = (sortType) => {
    let sortList = this.spacesTabList
    if (sortType === 'ASC') {
      this.spacesTabList = sortList.reverse()
    } else if (sortType === 'DESC') {
      this.spacesTabList = sortList.reverse()
    }
  }

  addTagTabLocally = async (spaceId, tag) => {
    this.spacesTabList = (this.spacesTabList || []).map((space) => {
      if (space.id === spaceId) {
        space.tagId = tag?.id
        space.spaceTag = tag
        return space
      } else {
        return space
      }
    })
  }

  @action.bound
  reorderSpacesTab = async ({
    projectId,
    newSpaces = this.spacesTabList,
  } = {}) => {
    if (newSpaces?.length < 2) {
      return [true, undefined]
    }
    try {
      let response = await this.post(API_ENDPOINTS.REORDER_SPACE, {
        projectId,
        newSpaceIdsInOrder: newSpaces.map((space) => space?.id).filter(Boolean),
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  importSpaceWithEquipment = async (values) => {
    try {
      this.importSpaceWithTaskFileLoader = true
      let formDataValues = generateFormData(values)
      let response = await this.postWithFormData(
        API_ENDPOINTS.IMPORT_SPACE_TEMP_WITH_EQUIPMENT,
        formDataValues
      )
      if (response.status) {
        this.importSpaces = response.data || []
        this.importSpacesWithEquipmentFailures =
          {
            headerMessage: response?.message || '',
            failedErrors: response.data?.failures || [],
            fileUpload: response.status,
            isError: true,
          } || []
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      if (err?.error?.statusCode === ERROR_CODE?.MENUAL_CODE) {
        this.importSpacesWithEquipmentFailures =
          {
            headerMessage: err?.message,
            failedErrors: null,
            fileUpload: false,
            isError: true,
          } || []
        return [false, undefined]
      } else {
        this.importSpacesWithEquipmentFailures =
          {
            headerMessage: err?.message,
            failedErrors: err?.error,
            fileUpload: false,
            isError: true,
          } || []
        return [false, undefined]
      }
    } finally {
      this.importSpaceWithTaskFileLoader = false
    }
  }

  @action.bound
  importSpaceWithTaskFile = async (values) => {
    try {
      this.importSpaceWithTaskFileLoader = true
      let formDataValues = generateFormData(values)
      let response = await this.postWithFormData(
        API_ENDPOINTS.IMPORT_SPACE_WITH_TASK_FILE,
        formDataValues
      )
      if (response.status) {
        this.importSpaces = response.data || []
        this.importSpacesFailures =
          {
            headerMessage: response?.message || '',
            failedErrors: response.data?.failures || [],
            fileUpload: response.status,
            isError: true,
          } || []
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      if (err?.error?.statusCode === ERROR_CODE?.MENUAL_CODE) {
        this.importSpacesFailures =
          {
            headerMessage: err?.message,
            failedErrors: null,
            fileUpload: false,
            isError: true,
          } || []
        return [false, undefined]
      } else {
        this.importSpacesFailures =
          {
            headerMessage: err?.message,
            failedErrors: err?.error,
            fileUpload: false,
            isError: true,
          } || []
        return [false, undefined]
      }
    } finally {
      this.importSpaceWithTaskFileLoader = false
    }
  }

  @action.bound
  importSpaceWithTaskFileReset = async () => {
    this.importSpaces = []
    this.importSpacesFailures = []
    this.importSpacesWithEquipmentFailures = []
  }

  @action.bound
  resetStore = () => {
    this.whichProject = {}
    this.spaces = []
    this.isUserAssignedSpaceCard = false
    this.isCheckAll = false
    this.spaceDownloadFileList = []
    this.newSpaceList = []
    this.spacesTotal = 0
    this.spacesCurrentPage = DEFAULT_PAGE_START
    this.tags = []
    this.spaceTemplates = []
    this.spaceLoader = false
    this.spaceTagLoader = false
    this.spacesSearchText = undefined
    this.ordering = undefined
    this.orderingBy = undefined
    this.spacesFilters = {}
    this.spacesTabListSearchText = undefined
    this.spacesTabList = []
    this.spaceListIsMultiSelect = false
    this.spaceListDownloadFileList = []
    this.spaceListDownloadCategory = []
    this.spacesTabListTotal = {}
    this.isUserAssignedSpaceList = false
    this.spacesTabListFilters = {}

    this.spacesFullList = []
    this.spacesFullListLoader = false
    this.addSpaceTemplateLoader = false

    this.importSpaces = []
    this.importSpacesFailures = []
    this.importSpacesWithEquipmentFailures = []
    this.importSpaceWithTaskFileLoader = false
    this.bulkSpaceLoader = false
  }
}
